"use client";

import React, { useEffect, useState } from "react";
import { Loader2, Check, X } from "lucide-react";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { HiOutlineUserGroup } from "react-icons/hi2";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";

const API_URL = REST_API_URL;

export default function ShowWorkersModal({ service, onClose, onUpdated }) {
  const { t } = useTranslation();
  const [allWorkers, setAllWorkers] = useState([]);
  const [selectedWorkers, setSelectedWorkers] = useState(service.workers || []);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    if (!service) return;

    const fetchWorkers = async () => {
      try {
        setLoading(true);
        const headers = buildHeaders();
        const res = await fetch(`${API_URL}/auth/workers-list/`, { headers });
        if (!res.ok) throw new Error("Failed to load workers");
        const data = await res.json();
        setAllWorkers(
          data.filter((w) => w.full_name && w.full_name.trim() !== ""),
        );
      } catch (err) {
        console.error(err);
        toast.error(t("home.failedLoadWorkers"));
      } finally {
        setLoading(false);
      }
    };

    fetchWorkers();
  }, [service, token, t]);

  const toggleWorker = (id) => {
    setSelectedWorkers((prev) =>
      prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id],
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const headers = buildHeaders();
      const res = await fetch(`${API_URL}/service/my-services/${service.id}/`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ ...service, workers: selectedWorkers }),
      });
      if (!res.ok) throw new Error("Failed to update workers");

      if (selectedWorkers.length === 0) {
        toast.success(t("home.serviceOffNoWorkers"));
        onUpdated({ ...service, workers: selectedWorkers, on_off: false });
        return;
      }

      toast.success(t("home.workersAddedSuccess"));
      onUpdated();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(t("home.failedUpdateWorkers"));
    } finally {
      setSaving(false);
    }
  };

  if (!service) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="modal-backdrop"
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          key="modal-content"
          className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 flex flex-col gap-5"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {t("home.addWorkerTitle")}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">
                {service.name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Worker list */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10">
              <Loader2 className="w-8 h-8 animate-spin text-[#020637] mb-3" />
              <p className="text-gray-500">{t("home.loadingWorkers")}</p>
            </div>
          ) : allWorkers.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <HiOutlineUserGroup className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">{t("home.noWorkersAssigned")}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-h-80 overflow-y-auto pr-1">
              <AnimatePresence>
                {allWorkers.map((worker) => {
                  const isSelected = selectedWorkers.includes(worker.id);
                  return (
                    <motion.div
                      key={worker.id}
                      layout
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                        isSelected
                          ? "bg-[#eef0f8] border border-[#020637]/20"
                          : "bg-gray-50 hover:bg-gray-100"
                      }`}
                      onClick={() => toggleWorker(worker.id)}
                    >
                      <div className="relative flex-shrink-0">
                        <img
                          src={
                            worker.image_url ||
                            "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                          }
                          alt={worker.full_name || t("home.unnamedWorker")}
                          className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                        />
                        {isSelected && (
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#020637] rounded-full border-2 border-white flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="font-semibold text-gray-900">
                          {worker.full_name || t("home.unnamedWorker")}
                        </span>
                        <span className="text-sm text-gray-500 truncate">
                          {worker.worker_email}
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}

          {/* Footer buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              {t("home.cancel")}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#020637] to-[#1e3aa0] text-white font-medium hover:from-[#0e1c7a] hover:to-[#1a2a8a] transition-all shadow-lg shadow-[#020637]/30 disabled:opacity-60 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("home.linkToService")}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
